/**
 * ScopeOfWorkSection
 *
 * Read-only display of firm's active services, enabled workflows, and guidelines.
 * Collapsed by default to save vertical space.
 */
import { Briefcase, Check, ChevronDown } from 'lucide-react';
import { useMemo } from 'react';

import { useFirmServicesWithCatalogue } from '@/components/services/hooks';
import type {
  DisplayServiceWithWorkflows,
  DisplayWorkflow,
} from '@/components/services/types';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

interface ScopeOfWorkSectionProps {
  firmId: string | undefined;
}

interface ServiceBlockProps {
  service: DisplayServiceWithWorkflows;
}

const WorkflowItem = ({ workflow }: { workflow: DisplayWorkflow }) => {
  const hasNotes = workflow.notes && workflow.notes.trim().length > 0;

  return (
    <div className="rounded px-2 py-1.5 hover:bg-white dark:hover:bg-zinc-800 transition-colors">
      <div className="flex items-start gap-1.5">
        <Check className="h-3 w-3 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-zinc-900 dark:text-white">
            {workflow.display_name}
          </div>
          {hasNotes && (
            <div className="mt-0.5 text-xs leading-snug text-zinc-600 dark:text-zinc-400 italic">
              {workflow.notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ServiceBlock = ({ service }: ServiceBlockProps) => {
  const enabledWorkflows = service.workflows.filter((w) => w.is_enabled);

  if (enabledWorkflows.length === 0) return null;

  return (
    <div className="space-y-0.5">
      <div className="text-xs font-semibold text-zinc-600 dark:text-zinc-400 px-2">
        {service.display_name}
      </div>
      <div className="space-y-0.5">
        {enabledWorkflows.map((workflow: DisplayWorkflow) => (
          <WorkflowItem key={workflow.workflow_code} workflow={workflow} />
        ))}
      </div>
    </div>
  );
};

export const ScopeOfWorkSection = ({ firmId }: ScopeOfWorkSectionProps) => {
  const { services, isLoading } = useFirmServicesWithCatalogue(firmId ?? '');

  // Filter to only active services with at least one enabled workflow
  const activeServices = useMemo(() => {
    return services.filter(
      (s) => s.is_active && s.workflows.some((w) => w.is_enabled),
    );
  }, [services]);

  // Count total enabled workflows
  const enabledWorkflowCount = useMemo(() => {
    return activeServices.reduce(
      (sum, s) => sum + s.workflows.filter((w) => w.is_enabled).length,
      0,
    );
  }, [activeServices]);

  // Don't render if no firmId
  if (!firmId) {
    return null;
  }

  return (
    <Collapsible defaultOpen={false}>
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 overflow-hidden">
        {/* Header */}
        <CollapsibleTrigger
          className={cn(
            'flex items-center justify-between w-full px-3 py-2',
            'bg-white dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-800',
            'hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors',
          )}
        >
          <div className="flex items-center gap-1.5">
            <Briefcase className="h-3.5 w-3.5 text-teal-600 dark:text-teal-400" />
            <h3 className="text-xs font-semibold text-zinc-900 dark:text-white">
              Firm Scope of Work
            </h3>
            {!isLoading && enabledWorkflowCount > 0 && (
              <span className="text-xs text-zinc-500 dark:text-zinc-400">
                {enabledWorkflowCount}
              </span>
            )}
          </div>
          <ChevronDown className="h-3 w-3 text-zinc-400 dark:text-zinc-500 transition-transform [[data-state=open]>&]:rotate-180" />
        </CollapsibleTrigger>

        {/* Content */}
        <CollapsibleContent>
          <div className="p-1.5 space-y-1.5">
            {isLoading && (
              <div className="flex items-center gap-2 px-2 py-1.5">
                <div className="h-3 w-3 animate-spin rounded-full border-2 border-zinc-300 dark:border-zinc-600 border-t-zinc-600 dark:border-t-zinc-400" />
                <span className="text-xs text-zinc-500 dark:text-zinc-400">
                  Loading...
                </span>
              </div>
            )}
            {!isLoading && activeServices.length === 0 && (
              <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400 px-2 py-1.5">
                No active services configured
              </div>
            )}
            {!isLoading &&
              activeServices.length > 0 &&
              activeServices.map((service) => (
                <ServiceBlock key={service.service_id} service={service} />
              ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
